import { SwarovskiOptikSegmentShort, jsHooks, type ContactDataType, type Link } from "@swo/shared";
import {
  NewHeaderData,
  SwarovskiOptikLocaleFrontend,
  type HeaderLanguagePicker,
  type HeaderSegmentSelector,
  type HeaderTheme,
} from "@swo/storefront";
import React, { useCallback, useEffect, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { usePrevious } from "../../hooks";
import { useOnRouteChange } from "../../hooks/use-on-route-change";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { HeaderT } from "./HeaderT";
import { MinimalisticHeadroomHeaderComponent } from "./MinimalisticHeadroomHeaderComponent";
import { MainHeaderComponent } from "./subcomponents/MainHeaderComponent";
import { SwarovskiHomepageLink } from "./subcomponents/SwarovskiLogoComponent";
import { useStore } from "../../store/hooks/use-store";
import { useCartContext } from "../../global/cart-context/cart-context";
import { usePageContext } from "../../global/page-context";

export type HeaderHooks = {
  onPin?: () => void;
  onUnpin?: () => void;
  onUnfix?: () => void;
};

export type ContextlessHeaderComponentProps = {
  segmentSelector: HeaderSegmentSelector;
  contactData: ContactDataType;
  theme: HeaderTheme;
  languagePicker: HeaderLanguagePicker;
  version: "normal" | "simplified" | "minimalistic";
  floatingDisabled?: boolean;
  locale: SwarovskiOptikLocaleFrontend;
  segment: SwarovskiOptikSegmentShort;
  links: HeaderLinks;
  headerHooks?: HeaderHooks;
  t: HeaderT;
  header: NewHeaderData;
  quickLinks: Array<Link>;
};

export type HeaderLinks = {
  homepage: SwarovskiHomepageLink;
  cartPage: string | null;
  dealerLocatorPage: string;
  searchPage: string;
  user: { login: string; register: string; profile: string };
};

const ContextLessHeaderComponentRaw: ReactTFC<ContextlessHeaderComponentProps> = ({ t, ...props }) => {
  const [navigation, setNavigation] = useState<"left" | "right" | null>(null);
  const previousNavigation = usePrevious(navigation);
  const store = useStore();

  const cartCtx = useCartContext();

  const hasAnythingInCart =
    cartCtx && cartCtx.cart._tag === "Right" ? (cartCtx.cart.right.cart?.lineItems.length ?? 0) > 0 : false;

  const page = usePageContext();
  const isOnCartPage = page.pageBase.__typename === "CartPage";

  const refLeft = useRef<HTMLButtonElement | null>(null);
  const refRight = useRef<HTMLButtonElement | null>(null);

  useOnRouteChange(() => {
    setNavigation(null);
  }, []);

  useEffect(() => {
    if (navigation === null) {
      if (previousNavigation == "left") {
        refLeft.current?.focus();
      } else if (previousNavigation == "right") {
        refRight.current?.focus();
      }
    }
  }, [navigation]);

  const onCloseButtonClicked = useCallback(() => {
    setNavigation(null);
  }, [setNavigation]);

  const mainNavigationCommonProps = {
    t: t,
    onCloseButtonClicked: onCloseButtonClicked,
    links: props.links,
    segmentSelector: props.segmentSelector.segments,
    contactData: props.contactData,
    locale: props.locale,
    segment: props.segment,
  };

  const mainNavigationElements =
    props.version === "normal"
      ? [
          {
            ref: refRight,
            label: t("products.iconLabel"),
            className: jsHooks.headerNavigationProducts,
            tag: "shop" as const,
          },
          {
            ref: refLeft,
            label: t("comeCloser.iconLabel"),
            className: jsHooks.headerNavigationContent,
            tag: "content" as const,
          },
        ]
      : [];

  if (props.version === "minimalistic") {
    return <MinimalisticHeadroomHeaderComponent homepageLink={props.links.homepage} />;
  }

  return (
    <MainHeaderComponent
      key={store.state.userPreferredSegmentKey}
      floatingDisabled={props.floatingDisabled}
      mainNavigationElements={mainNavigationElements}
      t={t}
      links={mainNavigationCommonProps.links}
      contactData={props.contactData}
      headerHooks={props.headerHooks}
      segments={{
        all: props.segmentSelector.segments,
        current: props.segment,
      }}
      locale={props.locale}
      header={props.header}
      userPreferredSegmentKey={store.state.userPreferredSegmentKey}
      showSegmentSelector={store.state.showSegmentSelector}
      hasAnythingInCart={hasAnythingInCart}
      isOnCartPage={isOnCartPage}
      quickLinks={props.quickLinks}
    />
  );
};

export const ContextLessHeaderComponent = React.memo(ContextLessHeaderComponentRaw, isEqual);
